import { type SearchClubItemDto } from '@customTypes/types';
import { ArrowRightIcon, MapPinIcon } from '@heroicons/react/24/outline';
import {
  Avatar,
  Badge,
  Card,
  Flex,
  Image,
  Space,
  Text,
  Title
} from '@mantine/core';
import { useRatingsDueQuery } from '@shared/api/requests/artist/rating/get';
import { useProfileStore } from '@shared/store/profileStore.ts';
import { ClubRating } from '@shared/utils/ClubRating.tsx';
import { FormattedMessage } from 'react-intl';

import { resolveImagePath } from '@/shared/helper';
import { translate } from '@/shared/i18n/translate';
import { LBActionButton } from '@/shared/ui-kit/components';

import styles from './ClubCard.module.css';

type LBClubCardProps = { data: SearchClubItemDto; navigate?: () => void };

export const LBClubCard = ({ data, navigate, ...rest }: LBClubCardProps) => {
  const artistId = useProfileStore((state) => state.profile?.profileIds[0]);

  const { data: dueItemsData } = useRatingsDueQuery({
    artistProfileId: artistId?.toString() || ''
  });

  if (!artistId) return null;

  return (
    <Card className={styles.container} onClick={navigate} {...rest}>
      <Flex align='center' gap={12}>
        {data.avatarPhoto ? (
          <Avatar
            radius='xl'
            size={56}
            src={
              data?.avatarPhoto.filePath &&
              resolveImagePath(data?.avatarPhoto.filePath)
            }
          />
        ) : (
          <Avatar radius='xl' size={56}>
            {data?.name.slice(0, 2)}
          </Avatar>
        )}

        {/* Name + Rating Row */}

        <Flex direction='column' gap={6} style={{ flex: 2 }}>
          <Flex align='center' justify='space-between' wrap='wrap'>
            <Title order={5} style={{ lineHeight: 1.3 }}>
              {data.name}
            </Title>
            {artistId && (
              <ClubRating
                averageRating={data?.ratingAverage?.totalAvg || 0}
                clubProfileId={data?.clubProfileId}
                dueItems={dueItemsData?.data ?? []}
                mode='artist'
                ratingAverage={data?.ratingAverage ?? null}
              />
            )}
          </Flex>
          <Flex direction='row' gap={8} wrap='nowrap'>
            {data.isHostessVacancyOpen && (
              <Text c='blue' fz={14}>
                <FormattedMessage id='commons.artistJobs.Hostess' />
              </Text>
            )}
            {data.isGoGoVacancyOpen && (
              <Text c='blue' fz={14}>
                <FormattedMessage id='commons.artistJobs.GoGo' />
              </Text>
            )}
            {data.isStripVacancyOpen && (
              <Text c='blue' fz={14}>
                <FormattedMessage id='commons.artistJobs.Strip' />
              </Text>
            )}
          </Flex>
        </Flex>
        {/* <CreateConversationButton
        recipientProfileId={data.clubProfileId.toString()}
        recipientProfileRole={Role.cl}
      >
        <ActionIcon variant='transparent'>
          <EnvelopeIcon height={24} width={24} />
        </ActionIcon>
      </CreateConversationButton> */}
      </Flex>
      <Space h={10} />
      <Card.Section className={styles.imageContainer}>
        {data.searchPhoto?.filePath !== undefined && (
          <Image
            alt='artis image'
            className={styles.image}
            height={300}
            src={resolveImagePath(data.searchPhoto.filePath)}
          />
        )}
        {data.isHiring && (
          <Badge bg='green' c='white' className={styles.badge}>
            {translate('commons.artistHiringIsOpen')}
          </Badge>
        )}

        <LBActionButton className={styles.actionIcon} onClick={navigate}>
          <ArrowRightIcon height={18} width={18} />
        </LBActionButton>
      </Card.Section>

      <Flex align='end' justify='space-between' pt={16}>
        <Flex align='center' gap={4} justify='end'>
          <MapPinIcon height={18} width={18} />
          <Text c='gray' fz={14}>
            {data.country}
          </Text>
        </Flex>
      </Flex>
    </Card>
  );
};
